import { useState, useEffect, useRef, useCallback } from "react";
import { product } from "../../../api/paquetes";
import { providers } from "../../../api/providers";
import { useNotification } from "../../../context/notification.context";
import dayjs from "dayjs";

interface UseProductosGrupalesProps {
  category?: string;
  terminoBusqueda?: string;
  refreshTrigger?: number;
  modoVisualizacion?: "vigentes" | "finalizados";
}

export const useProductosGrupales = ({
  category,
  terminoBusqueda = "",
  refreshTrigger = 0,
  modoVisualizacion = "vigentes",
}: UseProductosGrupalesProps) => {
  const { getError, getSuccess } = useNotification();

  const getCurrentUsername = () => {
    try {
      const u = (localStorage.getItem("current_username") || "").trim();
      return u || "SISTEMA";
    } catch {
      return "SISTEMA";
    }
  };

  // --- REFS & STATES ---
  const originalPaqueteRef = useRef<{
    concepto?: string;
    descripcion?: string;
    precioVenta?: number;
    moneda?: string;
  } | null>(null);

  const [productos, setProductos] = useState<any[]>([]);
  const [productosFiltrados, setProductosFiltrados] = useState<any[]>([]);
  const [cargando, setCargando] = useState(true);
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [productoAEliminar, setProductoAEliminar] = useState<number | null>(null);
  const [fichaAbierta, setFichaAbierta] = useState(false);
  const [productoSeleccionado, setProductoSeleccionado] = useState<any>(null);
  const [senaAbierta, setSenaAbierta] = useState(false);
  const [productoEnSena, setProductoEnSena] = useState<any>(null);
  const [guardandoCambios, setGuardandoCambios] = useState(false);
  const [proveedoresLista, setProveedoresLista] = useState<Array<{ id: number; nombre: string }>>([]);

  const [nuevoPasajero, setNuevoPasajero] = useState({ nombre: "", dni: "", pasaporte: "", fechaNacimiento: "" });
  const [nuevoPago, setNuevoPago] = useState({
    concepto: "",
    moneda: "USD",
    monto: "",
    tipoCambio: "",
    responsable_nombre: getCurrentUsername(),
    revisado_por_nombre: "",
  });
  const [nuevaObservacion, setNuevaObservacion] = useState(() => ({
    descripcion: "",
    fecha: "",
    usuario: getCurrentUsername(),
  }));

  // --- HELPERS ---
  const todayISO = () => new Date().toISOString().split("T")[0];

  const buildObs = (descripcion: string, usuario?: string, fecha?: string) => ({
    descripcion,
    fecha: fecha || todayISO(),
    usuario: (usuario || "").trim() || getCurrentUsername(),
  });

  const parseMetadata = (observaciones: any[]) => {
    const metaObs = observaciones.find((o: any) => o.descripcion?.startsWith("METADATA_GRUPAL:"));
    if (!metaObs) return {};
    try {
      return JSON.parse(metaObs.descripcion.replace("METADATA_GRUPAL: ", ""));
    } catch {
      return {};
    }
  };

  const mapPasajero = (p: any) => ({
    id: p.id,
    nombre: p.nombre || p.apellido_nombre || "-",
    dni: p.dni || p.documento || "-",
    pasaporte: p.pasaporte || "",
    fechaNacimiento: p.fecha_nacimiento || p.fecha_nac || "",
    colegio: p.colegio || p.institucion || p.escuela || "",
    viaja: (p.viaja === "0" || p.viaja === "false" || p.viaja === false || p.viaja === 0) ? false : (p.viaja !== undefined && p.viaja !== null ? Boolean(p.viaja) : true),
    // Nuevos campos para coincidir con el diálogo de edición completo
    apellido: p.apellido || "",
    nombre_solamente: p.nombre_solamente || "",
    direccion: p.direccion || "",
    ciudad: p.ciudad || "",
    telefono_fijo: p.telefono_fijo || "",
    celular_personal: p.celular_personal || "",
    email: p.email || "",
    cuil: p.cuil || "",
    sexo: p.sexo || "",
    emision: p.emision || "",
    talle: p.talle || "",
    colectivo: p.colectivo || "",
    turno: p.turno || "",
    bolso: Boolean(p.bolso),
    dni_o_pasaporte: Boolean(p.dni_o_pasaporte),
    foto_visa: Boolean(p.foto_visa),
    partida_de_nacimiento: Boolean(p.partida_de_nacimiento),
    permiso: Boolean(p.permiso),
    dni_fisico: Boolean(p.dni_fisico),
    fotocopia_de_dni: Boolean(p.fotocopia_de_dni),
    ficha_medica: Boolean(p.ficha_medica),
    vacuna: Boolean(p.vacuna),
    visa: Boolean(p.visa),
    pasaporte_europeo: Boolean(p.pasaporte_europeo),
    monto_producto: p.monto_producto || 0,
    monto_suplementario: p.monto_suplementario || 0,
    monto_percepcion: p.monto_percepcion || 0,
    porcentaje_liberado: p.porcentaje_liberado || 0,
    nombre_del_pagador: p.nombre_del_pagador || "",
    tipo_pago: p.tipo_pago || "",
    categoria_pago: p.categoria_pago || "",
    pago_confirmado: Boolean(p.pago_confirmado),
    datos_del_pagador: p.datos_del_pagador || "",
    dni_responsable: p.dni_responsable || "",
    parentesco_responsable: p.parentesco_responsable || "",
    celular_contacto_1: p.celular_contacto_1 || "",
    dueno_celular_1: p.dueno_celular_1 || "",
    celular_contacto_2: p.celular_contacto_2 || "",
    dueno_celular_2: p.dueno_celular_2 || "",
    responsable_apellido: p.responsable_apellido || "",
    responsable_direccion: p.responsable_direccion || "",
    firmo_contrato: Boolean(p.firmo_contrato),
    fecha_firma: p.fecha_firma || "",
    observaciones: p.observaciones || p.observacion || "",
    dni_imagen: p.dni_imagen || "",
    pasaporte_imagen: p.pasaporte_imagen || "",
  });

  const calcularPasajerosViajan = (lista: any[]) => lista.filter(p => Boolean(p.viaja)).length;

  // --- PRODUCT LIST LOADING ---
  const cargarProductos = useCallback(async () => {
    try {
      setCargando(true);
      const response = await product.getAll();
      const paquetesAPI = response.data?.data || response.data || [];

      const paquetes = paquetesAPI
        .filter((p: any) => {
          const hasTag = p.descripcion?.includes("[GRUPAL:");
          const hasObs = p.observaciones?.includes("METADATA_GRUPAL");
          const isGrupal = p.is_grupal || hasTag || hasObs;

          if (!isGrupal) return false;
          if (!category) return true;

          const tagMatch = p.descripcion?.includes(`[GRUPAL:${category}]`);
          const obsMatch = p.observaciones?.includes(`"categoria":"${category}"`);
          const catFieldMatch = p.categoria === category;

          return tagMatch || obsMatch || catFieldMatch;
        })
        .map((p: any) => {
          let obsArr: any[] = [];
          try { obsArr = typeof p.observaciones === "string" ? JSON.parse(p.observaciones) : (p.observaciones || []); } catch {}
          
          const metadata = parseMetadata(obsArr);
          const anioViaje = metadata.anioViaje || metadata.anioEvento || (p.fecha_salida ? new Date(p.fecha_salida).getFullYear().toString() : "");
          
          const tagMatch = (p.descripcion || "").match(/\[GRUPAL:(.*?)\]/);
          const finalCategory = (p.categoria || metadata.categoria || (tagMatch ? tagMatch[1] : category) || "otros").toLowerCase();

          return {
            id: p.id,
            numero: p.id,
            estado: p.estado || "Vendido",
            fechaVenta: p.created_at ? new Date(p.created_at) : new Date(),
            fechaSalida: p.fecha_salida ? new Date(p.fecha_salida) : null,
            vendedor: p.vendedor_nombre || p.vendedor || "Sistema",
            cliente: p.cliente?.nombre || "Sin cliente",
            concepto: p.concepto || "Sin concepto",
            precioVenta: parseFloat(p.importe) || 0,
            moneda: p.moneda || "USD",
            descripcion: (p.descripcion || "").replace(/\[GRUPAL:.*?\]/g, "").trim(),
            anioViaje,
            adultos: parseInt(p.adultos) || metadata.cantidadPasajeros || 0,
            categoria: finalCategory,
            categoriaFormateada: (function() {
              if (["quince", "quinces"].includes(finalCategory)) return "Quinceañeras";
              if (finalCategory === "egresados") return "Egresados";
              if (finalCategory === "educativos") return "Educativos";
              if (finalCategory === "eventos") return "Eventos";
              return "Otros";
            })(),
            pasajerosLista: [], // Placeholder until loaded
          };
        });

      setProductos(paquetes);
      setProductosFiltrados(paquetes);
      setCargando(false);
    } catch (error) {
      getError("Error al cargar los paquetes grupales");
      setCargando(false);
    }
  }, [getError, category]);

  useEffect(() => {
    cargarProductos();
  }, [cargarProductos, refreshTrigger]);

  useEffect(() => {
    let filtrados = productos;
    if (modoVisualizacion === "finalizados") {
      filtrados = filtrados.filter(
        (p) => p.estado === "Finalizado" || p.estado === "Cancelado",
      );
    } else {
      filtrados = filtrados.filter(
        (p) => p.estado !== "Finalizado" && p.estado !== "Cancelado",
      );
    }
    if (terminoBusqueda.trim() !== "") {
      filtrados = filtrados.filter((producto) =>
        Object.values(producto).some((valor) =>
          String(valor).toLowerCase().includes(terminoBusqueda.toLowerCase()),
        ),
      );
    }
    setProductosFiltrados(filtrados);
  }, [productos, modoVisualizacion, terminoBusqueda]);

  useEffect(() => {
    if (fichaAbierta) {
      const cargarProveedores = async () => {
        try {
          const response = await providers.getAll();
          const dataArr: any[] = Array.isArray(response.data)
            ? response.data
            : [];
          const lista = dataArr
            .map((p: any) => ({
              id: p.id,
              nombre: (p?.nombre || "").toString().trim(),
            }))
            .filter((p) => !!p.nombre);
          setProveedoresLista(lista);
        } catch {
          setProveedoresLista([]);
        }
      };
      cargarProveedores();
    }
  }, [fichaAbierta]);

  // --- PRODUCT DETAIL HANDLING ---
  const handleViewProduct = async (producto: any) => {
    setProductoSeleccionado(producto);
    setFichaAbierta(true);

    try {
      const response = await product.getById(producto.id);
      const pkg = response.data?.data || response.data;
      
      // Mapear Servicios
      const aereos = (pkg.aereosPaquetes || []).map((s: any) => ({ ...s, tipo: "Aéreo", precioVenta: parseFloat(s.importe) || 0, precioCosto: parseFloat(s.importe_costo) || 0, nroFile: s.nro_file || "" }));
      const hoteles = (pkg.hotelesPaquetes || []).map((s: any) => ({ ...s, tipo: "Hotel", precioVenta: parseFloat(s.importe) || 0, precioCosto: parseFloat(s.importe_costo) || 0, nroFile: s.nro_file || "" }));
      const extras = (pkg.serviciosPaquetes || []).map((s: any) => ({ ...s, tipo: "Extra", precioVenta: parseFloat(s.importe) || 0, precioCosto: parseFloat(s.importe_costo) || 0, nroFile: s.nro_file || "" }));
      const servicios = [...aereos, ...hoteles, ...extras];

      // Mapear Observaciones y Metadata
      let observacionesArr: any[] = [];
      try { observacionesArr = typeof pkg.observaciones === "string" ? JSON.parse(pkg.observaciones) : (pkg.observaciones || []); } catch {}
      const metadata = parseMetadata(observacionesArr);

      // Mapear Pagos y Pasajeros
      const pagos = (pkg.pagosPaquetes || []).filter((p: any) => !p.deleted_at).map((p: any) => ({
        ...p,
        importe: parseFloat(p.importe || "0"),
        tipoCambio: p.tipoCambio ?? p.tipo_cambio ?? p.tc ?? null,
      }));
      
      const pasajerosLista = (pkg.pasajerosPaquetes || pkg.pasajeros || []).filter((p: any) => !p.deleted_at).map(mapPasajero);

      // Totales
      const totalCostoServicios = servicios.reduce((acc, s) => acc + (s.precioCosto || 0), 0);
      const totalVentaMetadata = (parseFloat(metadata.montoServicio) || 0) + (parseFloat(metadata.montoSuplementario) || 0) + (parseFloat(metadata.percepcion) || 0);
      const precioVentaCalculado = totalVentaMetadata || parseFloat(pkg.importe) || 0;

      const dataCompleta = {
        ...producto,
        ...pkg,
        precioVenta: precioVentaCalculado,
        precioCosto: metadata.totalCostoManual || totalCostoServicios,
        servicios,
        pagos,
        pasajerosLista,
        pasajerosViajan: calcularPasajerosViajan(pasajerosLista),
        adultos: parseInt(pkg.adultos) || metadata.cantidadPasajeros || pasajerosLista.length,
        observaciones: observacionesArr,
        cliente_correo: pkg.cliente?.email || pkg.cliente?.correo || "-",
        cliente_telefono: pkg.cliente?.telefono || pkg.cliente?.celular || "-",
        categoria: pkg.categoria || category,
      };

      setProductoSeleccionado(dataCompleta);
      originalPaqueteRef.current = {
        concepto: dataCompleta.concepto,
        descripcion: dataCompleta.descripcion,
        precioVenta: dataCompleta.precioVenta,
        moneda: dataCompleta.moneda,
      };
    } catch (error) {
      getError("Error al cargar los detalles del paquete");
    }
  };

  const handleProductSheetClose = () => {
    setFichaAbierta(false);
    setProductoSeleccionado(null);
  };

  const handleDeleteProduct = (id: number) => {
    setProductoAEliminar(id);
    setDialogoAbierto(true);
  };

  const confirmDeleteProduct = async () => {
    try {
      if (productoAEliminar) {
        await product.delete(productoAEliminar);
        const productosActualizados = productos.filter(
          (p) => p.id !== productoAEliminar,
        );
        setProductos(productosActualizados);
        setProductosFiltrados(productosActualizados);
        getSuccess("Paquete eliminado correctamente");
        setDialogoAbierto(false);
        setProductoAEliminar(null);
      }
    } catch (error: any) {
      if (error?.response?.status === 404 || error?.response?.status === 204) {
        const productosActualizados = productos.filter(
          (p) => p.id !== productoAEliminar,
        );
        setProductos(productosActualizados);
        setProductosFiltrados(productosActualizados);
        getSuccess("Paquete eliminado correctamente");
        setDialogoAbierto(false);
        setProductoAEliminar(null);
      } else {
        getError(
          error?.response?.data?.message || "Error al eliminar el paquete",
        );
        setDialogoAbierto(false);
      }
    }
  };

  const guardarCambios = async () => {
    if (!productoSeleccionado) return;
    setGuardandoCambios(true);
    try {
      const original = originalPaqueteRef.current;
      const observacionesActuales: any[] = Array.isArray(
        productoSeleccionado.observaciones,
      )
        ? [...productoSeleccionado.observaciones]
        : [];

      if (original) {
        const cambios: any[] = [];
        const conceptoActual = (productoSeleccionado.concepto ?? "").toString();
        const descripcionActual = (
          productoSeleccionado.descripcion ?? ""
        ).toString();
        const monedaActual = (productoSeleccionado.moneda ?? "USD").toString();

        if (conceptoActual !== (original.concepto ?? ""))
          cambios.push(
            buildObs(
              `Actualización paquete\nConcepto: ${conceptoActual} por ${original.concepto ?? ""}`,
            ),
          );
        if (descripcionActual !== (original.descripcion ?? ""))
          cambios.push(
            buildObs(
              `Actualización paquete\nDescripción: ${descripcionActual}`,
            ),
          );
        if (monedaActual !== (original.moneda ?? ""))
          cambios.push(
            buildObs(
              `Actualización paquete\nMoneda: ${monedaActual} por ${original.moneda ?? ""}`,
            ),
          );
        if (cambios.length > 0) observacionesActuales.push(...cambios);
      }

      // Agregar nueva observación si existe
      if (nuevaObservacion.descripcion.trim()) {
        observacionesActuales.push(buildObs(
          nuevaObservacion.descripcion,
          nuevaObservacion.usuario,
          nuevaObservacion.fecha
        ));
      }

      const metadataIdx = observacionesActuales.findIndex((o: any) =>
        o.descripcion?.startsWith("METADATA_GRUPAL:"),
      );
      let metadataGrupal: any = {};
      if (metadataIdx !== -1) {
        try {
          metadataGrupal = JSON.parse(
            observacionesActuales[metadataIdx].descripcion.replace(
              "METADATA_GRUPAL: ",
              "",
            ),
          );
        } catch (e) {}
      }
      if (productoSeleccionado.fechaSalida) {
        metadataGrupal.anioViaje = dayjs(productoSeleccionado.fechaSalida)
          .year()
          .toString();
      }
      metadataGrupal.totalCostoManual = productoSeleccionado.precioCosto;
      const metadataString = `METADATA_GRUPAL: ${JSON.stringify(metadataGrupal)}`;
      if (metadataIdx !== -1)
        observacionesActuales[metadataIdx].descripcion = metadataString;
      else observacionesActuales.push(buildObs(metadataString));

      let descripcionConTag = (productoSeleccionado.descripcion || "")
        .replace(/\[GRUPAL:.*?\]/g, "")
        .trim();
      const catFinal = productoSeleccionado.categoria || category;
      if (catFinal) {
        descripcionConTag = `[GRUPAL:${catFinal}] ${descripcionConTag}`;
      }

      const servicios = productoSeleccionado.servicios || [];
      const filterMapServ = (tipo: string) =>
        servicios
          .filter((s: any) => s.tipo === tipo)
          .map((s: any) => ({
            id: s.id,
            descripcion: s.descripcion,
            importe: s.precioVenta,
            moneda: s.moneda,
            importe_costo: s.precioCosto,
            moneda_costo: s.monedaCosto,
            nro_file: s.nroFile,
            revisado: s.revisado,
            revisado_costo: s.revisadoCosto,
          }));

      const importeFinal =
        productoSeleccionado.precioVenta > 0
          ? productoSeleccionado.precioVenta
          : original?.precioVenta || 0;

      const payload: any = {
        concepto: productoSeleccionado.concepto,
        descripcion: descripcionConTag,
        importe: importeFinal,
        moneda: productoSeleccionado.moneda,
        categoria: productoSeleccionado.categoria || category,
        fecha_salida: productoSeleccionado.fechaSalida
          ? productoSeleccionado.fechaSalida instanceof Date
            ? productoSeleccionado.fechaSalida.toISOString()
            : productoSeleccionado.fechaSalida
          : null,
        observaciones: JSON.stringify(observacionesActuales),
        adultos: parseInt(productoSeleccionado.adultos || 0),
        ninos: parseInt(productoSeleccionado.ninos || 0),
        infantes: parseInt(productoSeleccionado.infantes || 0),
        cliente_id:
          productoSeleccionado.cliente_id ||
          productoSeleccionado.cliente?.id ||
          null,
        cliente: {
          nombre: productoSeleccionado.cliente?.nombre || "",
          telefono: productoSeleccionado.cliente_telefono || "",
          email: productoSeleccionado.cliente_correo || "",
        },
        vendedor_id: productoSeleccionado.vendedor_id || null,
        estado: productoSeleccionado.estado || "Vendido",
        aereosPaquetes: filterMapServ("Aéreo"),
        hotelesPaquetes: filterMapServ("Hotel"),
        serviciosPaquetes: filterMapServ("Extra"),
        pagosPaquetes: (productoSeleccionado.pagos || []).map((p: any) => ({
          id: p.id,
          importe: p.importe,
          moneda: p.moneda || "ARS",
          observacion: p.observacion,
          created_at: p.created_at,
          responsable_nombre: p.responsable_nombre,
          revisado_por_nombre: p.revisado_por_nombre,
          fecha_revision: p.fecha_revision,
        })),
        pasajerosPaquetes: (productoSeleccionado.pasajerosLista || []).map((p: any) => ({
          id: p.id,
          nombre: p.nombre,
          dni: p.dni,
          colegio: p.colegio,
          viaja: p.viaja ? 1 : 0,
          apellido: p.apellido,
          nombre_solamente: p.nombre_solamente,
          direccion: p.direccion,
          ciudad: p.ciudad,
          telefono_fijo: p.telefono_fijo,
          celular_personal: p.celular_personal,
          email: p.email,
          cuil: p.cuil,
          sexo: p.sexo,
          emision: p.emision,
          talle: p.talle,
          colectivo: p.colectivo,
          turno: p.turno,
          bolso: p.bolso ? 1 : 0,
          dni_o_pasaporte: p.dni_o_pasaporte ? 1 : 0,
          foto_visa: p.foto_visa ? 1 : 0,
          partida_de_nacimiento: p.partida_de_nacimiento ? 1 : 0,
          permiso: p.permiso ? 1 : 0,
          dni_fisico: p.dni_fisico ? 1 : 0,
          fotocopia_de_dni: p.fotocopia_de_dni ? 1 : 0,
          ficha_medica: p.ficha_medica ? 1 : 0,
          vacuna: p.vacuna ? 1 : 0,
          visa: p.visa ? 1 : 0,
          pasaporte_europeo: p.pasaporte_europeo ? 1 : 0,
          monto_producto: p.monto_producto,
          monto_suplementario: p.monto_suplementario,
          monto_percepcion: p.monto_percepcion,
          porcentaje_liberado: p.porcentaje_liberado,
          nombre_del_pagador: p.nombre_del_pagador,
          tipo_pago: p.tipo_pago,
          categoria_pago: p.categoria_pago,
          pago_confirmado: p.pago_confirmado ? 1 : 0,
          datos_del_pagador: p.datos_del_pagador,
          dni_responsable: p.dni_responsable,
          parentesco_responsable: p.parentesco_responsable,
          celular_contacto_1: p.celular_contacto_1,
          dueno_celular_1: p.dueno_celular_1,
          celular_contacto_2: p.celular_contacto_2,
          dueno_celular_2: p.dueno_celular_2,
          cuit_pagador: p.cuit_pagador,
          responsable_apellido: p.responsable_apellido,
          responsable_direccion: p.responsable_direccion,
          firmo_contrato: p.firmo_contrato ? 1 : 0,
          fecha_firma: p.fecha_firma,
          observaciones: p.observaciones,
          dni_imagen: p.dni_imagen,
          pasaporte_imagen: p.pasaporte_imagen,
        })),
      };

      const updateResponse = await product.update(
        productoSeleccionado.id,
        payload,
      );

      // Limpiar nueva observación tras guardar
      if (nuevaObservacion.descripcion.trim()) {
        setNuevaObservacion({
            descripcion: "",
            fecha: "",
            usuario: getCurrentUsername(),
        });
      }
      // Intentar obtener el importe de la respuesta (puede estar en .data o .data.data)
      const dataResp = updateResponse.data?.data || updateResponse.data;
      const impResp = dataResp?.importe ? parseFloat(dataResp.importe) : NaN;
      const adultosResp = dataResp?.adultos ? parseInt(dataResp.adultos) : NaN;

      const productosActualizados = productos.map((p) =>
        p.id === productoSeleccionado.id
          ? {
              ...productoSeleccionado,
              precioVenta:
                !isNaN(impResp) && impResp > 0 ? impResp : importeFinal,
              adultos: !isNaN(adultosResp)
                ? adultosResp
                : parseInt(productoSeleccionado.adultos) || 0,
              pasajeros: !isNaN(adultosResp)
                ? adultosResp
                : parseInt(productoSeleccionado.adultos) || 0,
              anioViaje: productoSeleccionado.fechaSalida
                ? dayjs(productoSeleccionado.fechaSalida).year().toString()
                : p.anioViaje,
            }
          : p,
      );
      setProductos(productosActualizados);
      setProductosFiltrados(productosActualizados);
      getSuccess("Cambios guardados exitosamente");
      setFichaAbierta(false);
    } catch (error: any) {
      getError(
        error?.response?.data?.message || "Error al guardar los cambios",
      );
    } finally {
      setGuardandoCambios(false);
    }
  };

  const actualizarProductoSeleccionado = (actualizador: (prev: any) => any) => {
    setProductoSeleccionado((prev: any) => {
      const actualizado = actualizador(prev || {});
      setProductos((lista) =>
        lista.map((p) => (p.id === actualizado.id ? actualizado : p)),
      );
      setProductosFiltrados((lista) =>
        lista.map((p) => (p.id === actualizado.id ? actualizado : p)),
      );
      return actualizado;
    });
  };

  const calcularTotalesServicios = (
    servicios: any[],
    monedaPaquete: string,
  ) => {
    let tvUSD = 0,
      tvARS = 0,
      tcUSD = 0,
      tcARS = 0;
    servicios.forEach((s: any) => {
      if ((s.moneda || "USD").toUpperCase() === "USD")
        tvUSD += s.precioVenta || 0;
      else tvARS += s.precioVenta || 0;
      if ((s.monedaCosto || "USD").toUpperCase() === "USD")
        tcUSD += s.precioCosto || 0;
      else tcARS += s.precioCosto || 0;
    });
    const m = (monedaPaquete || "USD").toUpperCase();
    return {
      totalVenta: m === "USD" ? tvUSD : tvARS,
      totalCosto: m === "USD" ? tcUSD : tcARS,
    };
  };

  const actualizarServicio = (
    index: number,
    servicioActualizado: any,
    recalcularFinanzas = false,
  ) => {
    actualizarProductoSeleccionado((prev: any) => {
      const servicios = [...(prev?.servicios || [])];
      servicios[index] = servicioActualizado;
      const totales = recalcularFinanzas
        ? calcularTotalesServicios(servicios, prev?.moneda || "USD")
        : { totalVenta: prev?.precioVenta, totalCosto: prev?.precioCosto };
      return {
        ...prev,
        servicios,
        precioVenta: totales.totalVenta,
        precioCosto: totales.totalCosto,
      };
    });
    getSuccess("Servicio actualizado");
  };

  const agregarServicio = (nuevoServicio: any) => {
    actualizarProductoSeleccionado((prev: any) => {
      const servicios = [...(prev?.servicios || []), nuevoServicio];
      const totales = calcularTotalesServicios(
        servicios,
        prev?.moneda || "USD",
      );
      return {
        ...prev,
        servicios,
        precioVenta: totales.totalVenta,
        precioCosto: totales.totalCosto,
      };
    });
    getSuccess("Servicio agregado");
  };

  // --- PASSENGER MANAGEMENT ---
  const onAddPassenger = (nuevo: any) => {
    const pNormalizado = mapPasajero(nuevo);
    actualizarProductoSeleccionado((prev: any) => {
      const lista = [...(prev?.pasajerosLista || []), pNormalizado];
      return { 
        ...prev, 
        pasajerosLista: lista, 
        pasajerosViajan: calcularPasajerosViajan(lista),
        adultos: lista.length 
      };
    });
    getSuccess("Pasajero agregado");
  };

  const onUpdatePassenger = async (pasajeroEditado: any) => {
    const pNormalizado = mapPasajero(pasajeroEditado);
    actualizarProductoSeleccionado((prev: any) => {
      const lista = (prev?.pasajerosLista || []).map((p: any) => p.id === pNormalizado.id ? pNormalizado : p);
      return { 
        ...prev, 
        pasajerosLista: lista, 
        pasajerosViajan: calcularPasajerosViajan(lista) 
      };
    });
  };

  const eliminarPasajero = async (pasajero: any) => {
    try {
      await product.deletePasajero(productoSeleccionado.id, pasajero.id);
      actualizarProductoSeleccionado((prev: any) => {
        const lista = (prev?.pasajerosLista || []).filter((p: any) => p.id !== pasajero.id);
        return { 
          ...prev, 
          pasajerosLista: lista, 
          pasajeros: lista.length,
          pasajerosViajan: calcularPasajerosViajan(lista)
        };
      });
      getSuccess("Pasajero eliminado");
    } catch {
      getError("Error al eliminar pasajero");
    }
  };



  const handleOpenSena = async (prod: any) => {
    setProductoEnSena(prod);
    setSenaAbierta(true);
    try {
      const response = await product.getById(prod.id);
      const pkg = response.data?.data || response.data;
      
      const pagos = (pkg.pagosPaquetes || []).filter((p: any) => !p.deleted_at).map((p: any) => ({
        ...p,
        importe: parseFloat(p.importe || "0"),
      }));
      
      const pasajerosLista = (pkg.pasajerosPaquetes || pkg.pasajeros || []).filter((p: any) => !p.deleted_at).map(mapPasajero);

      const servicios = [
        ...(pkg.aereosPaquetes || []),
        ...(pkg.hotelesPaquetes || []),
        ...(pkg.serviciosPaquetes || []),
      ];
      const totalServicios = servicios.reduce((acc, s) => acc + (parseFloat(s.importe) || 0), 0);
      const precioVentaFinal = parseFloat(pkg.importe) || totalServicios || prod.precioVenta;
      
      let observacionesArr: any[] = [];
      try { observacionesArr = typeof pkg.observaciones === "string" ? JSON.parse(pkg.observaciones) : (pkg.observaciones || []); } catch {}

      const fullData = {
        ...prod,
        ...pkg,
        precioVenta: precioVentaFinal,
        pagos,
        pasajerosLista,
        pasajerosViajan: calcularPasajerosViajan(pasajerosLista),
        observaciones: observacionesArr,
      };

      setProductoEnSena(fullData);
      if (productoSeleccionado && productoSeleccionado.id === prod.id) {
        setProductoSeleccionado((prev: any) => ({ ...prev, ...fullData }));
      }
    } catch (e) {
      getError("Error al cargar datos del paquete para señas");
    }
  };

  const handleUpdateMontoSena = async (nuevoMonto: number) => {
    if (!productoEnSena) return;
    try {
      const resp = await product.getById(productoEnSena.id);
      const pkg = resp.data?.data || resp.data;
      const importeOriginal =
        parseFloat(pkg.importe) || productoEnSena.precioVenta || 0;
      let obs: any[] = [];
      try {
        obs =
          typeof pkg.observaciones === "string"
            ? JSON.parse(pkg.observaciones)
            : pkg.observaciones || [];
      } catch (e) {}
      const metadataIdx = obs.findIndex((o: any) =>
        o.descripcion?.startsWith("METADATA_GRUPAL:"),
      );
      let metadata: any = {};
      if (metadataIdx !== -1) {
        try {
          metadata = JSON.parse(
            obs[metadataIdx].descripcion.replace("METADATA_GRUPAL: ", ""),
          );
        } catch (e) {}
      }
      metadata.montoSena = nuevoMonto;
      const metadataStr = `METADATA_GRUPAL: ${JSON.stringify(metadata)}`;
      if (metadataIdx !== -1) obs[metadataIdx].descripcion = metadataStr;
      else obs.push(buildObs(metadataStr));

      await product.update(productoEnSena.id, {
        observaciones: JSON.stringify(obs),
        pagosPaquetes: (productoEnSena.pagos || []).map((p: any) => ({
          id: p.id,
          importe: p.importe,
          moneda: p.moneda || "ARS",
          observacion: p.observacion,
          created_at: p.created_at,
          responsable_nombre: p.responsable_nombre,
          revisado_por_nombre: p.revisado_por_nombre,
          fecha_revision: p.fecha_revision,
        })),
      });
      const respD = await product.getById(productoEnSena.id);
      const pkgD = respD.data?.data || respD.data;
      if (parseFloat(pkgD.importe) === 0 && importeOriginal > 0) {
        await product.update(productoEnSena.id, {
          concepto: pkgD.concepto,
          descripcion: pkgD.descripcion,
          importe: importeOriginal,
          moneda: pkgD.moneda,
          fecha_salida: pkgD.fecha_salida,
          observaciones: JSON.stringify(obs),
          adultos: pkgD.adultos || 0,
          ninos: pkgD.ninos || 0,
          infantes: pkgD.infantes || 0,
          cliente_id: pkgD.cliente_id || null,
          vendedor_id: pkgD.vendedor_id || null,
          estado: pkgD.estado || "Vendido",
          pagosPaquetes: (productoEnSena.pagos || []).map((p: any) => ({
            id: p.id,
            importe: p.importe,
            moneda: p.moneda || "ARS",
            observacion: p.observacion,
            created_at: p.created_at,
            responsable_nombre: p.responsable_nombre,
            revisado_por_nombre: p.revisado_por_nombre,
            fecha_revision: p.fecha_revision,
          })),
        });
      }
      const dataActualizada = {
        ...productoEnSena,
        ...pkg,
        observaciones: obs,
        precioVenta: importeOriginal,
      };
      setProductoEnSena(dataActualizada);
      if (productoSeleccionado && productoSeleccionado.id === productoEnSena.id)
        setProductoSeleccionado((prev: any) => ({
          ...prev,
          ...dataActualizada,
        }));
      setProductos((prev) =>
        prev.map((p) =>
          p.id === productoEnSena.id
            ? {
                ...productoEnSena,
                ...dataActualizada,
                precioVenta: importeOriginal,
              }
            : p,
        ),
      );
      setProductosFiltrados((prev) =>
        prev.map((p) =>
          p.id === productoEnSena.id
            ? {
                ...productoEnSena,
                ...dataActualizada,
                precioVenta: importeOriginal,
              }
            : p,
        ),
      );
      getSuccess("Seña actualizada correctamente");
      setSenaAbierta(false);
    } catch (e) {
      getError("Error al actualizar el monto de seña");
    }
  };



  const handleAddPagoPasajero = async (pasajero: any, pagoData: any) => {
    // Si la seña está abierta, trabajamos sobre productoEnSena. Si no, sobre productoSeleccionado (ficha).
    const target = senaAbierta ? productoEnSena : (productoSeleccionado || productoEnSena);
    
    if (!target) return;
    try {
      const resp = await product.createPago(target.id, {
        observacion: pagoData.observacion,
        moneda: "ARS",
        importe: pagoData.importe,
        created_at: pagoData.fecha,
        responsable_nombre: getCurrentUsername(),
      });
      const nuevoPago = {
        id: resp.data.id,
        observacion: resp.data.observacion,
        importe: parseFloat(resp.data.importe),
        moneda: resp.data.moneda,
        created_at: resp.data.created_at,
        responsable_nombre: resp.data.responsable_nombre,
        revisado_por_nombre: resp.data.revisado_por_nombre,
        fecha_revision: resp.data.fecha_revision,
      };
      
      if (productoEnSena?.id === target.id) {
        setProductoEnSena((prev: any) => ({
          ...prev,
          pagos: [...(prev.pagos || []), nuevoPago],
        }));
      }
      if (productoSeleccionado?.id === target.id) {
         actualizarProductoSeleccionado((prev: any) => ({
            ...prev,
            pagos: [...(prev.pagos || []), nuevoPago],
         }));
      }
      getSuccess(`Pago registrado para ${pasajero.nombre}`);
    } catch (e) {
      getError("Error al registrar pago del pasajero");
    }
  };

  const handleUpdatePagoEstudiante = async (pagoId: number, pagoData: any) => {
    const target = senaAbierta ? productoEnSena : (productoSeleccionado || productoEnSena);
    if (!target) return;
    try {
      const resp = await product.updatePago(target.id, pagoId, {
        ...pagoData,
        moneda: "ARS",
      });
      
      const updateList = (lista: any[]) => lista.map((p: any) =>
          p.id === pagoId
            ? {
                ...p,
                importe: parseFloat(resp.data.importe),
                observacion: resp.data.observacion,
                created_at: resp.data.created_at,
              }
            : p
        );

      if (productoEnSena?.id === target.id) {
        setProductoEnSena((prev: any) => ({
           ...prev,
           pagos: updateList(prev.pagos || [])
        }));
      }
      if (productoSeleccionado?.id === target.id) {
         actualizarProductoSeleccionado((prev: any) => ({
            ...prev,
            pagos: updateList(prev.pagos || [])
         }));
      }

      getSuccess("Pago actualizado correctamente");
    } catch (e) {
      getError("Error al actualizar el pago");
    }
  };

  const handleEliminarPagoEstudiante = async (pagoId: number) => {
    const target = senaAbierta ? productoEnSena : (productoSeleccionado || productoEnSena);
    if (!target) return;
    try {
      await product.deletePago(target.id, pagoId);
      
      const filterList = (lista: any[]) => lista.filter((p: any) => p.id !== pagoId);

      if (productoEnSena?.id === target.id) {
         setProductoEnSena((prev: any) => ({
            ...prev,
            pagos: filterList(prev.pagos || [])
         }));
      }
      if (productoSeleccionado?.id === target.id) {
         actualizarProductoSeleccionado((prev: any) => ({
            ...prev,
            pagos: filterList(prev.pagos || [])
         }));
      }
      
      getSuccess("Pago eliminado correctamente");
    } catch (e) {
      getError("Error al eliminar el pago");
    }
  };

  const handleToggleRevisadoEstudiante = async (
    pagoId: number,
    isChecked: boolean,
    fechaRevisado?: string,
  ) => {
    const target = senaAbierta ? productoEnSena : (productoSeleccionado || productoEnSena);
    if (!target) return;
    try {
      const pago = target.pagos.find((p: any) => p.id === pagoId);
      const response = await product.updatePago(target.id, pagoId, {
        ...pago,
        revisado_por_nombre: isChecked ? getCurrentUsername() : "",
        fecha_revision: fechaRevisado,
      });
      
      const resData = response.data?.data || response.data;
      const updateList = (lista: any[]) => lista.map((p: any) =>
          p.id === pagoId
            ? {
                ...p,
                revisado_por_nombre:
                  resData.revisado_por_nombre ||
                  (isChecked ? getCurrentUsername() : ""),
                fecha_revision: resData.fecha_revision || fechaRevisado,
              }
            : p
        );

      if (productoEnSena?.id === target.id) {
         setProductoEnSena((prev: any) => ({
            ...prev,
            pagos: updateList(prev.pagos || [])
         }));
      }
      if (productoSeleccionado?.id === target.id) {
         actualizarProductoSeleccionado((prev: any) => ({
            ...prev,
            pagos: updateList(prev.pagos || [])
         }));
      }
    } catch (e) {
      getError("Error al actualizar estado de revisión");
    }
  };

  const handleFinalizarProducto = async (id: number) => {
    try {
      setGuardandoCambios(true);
      await product.update(id, { estado: "Finalizado" });
      getSuccess("Producto finalizado correctamente");
      await cargarProductos();
      setFichaAbierta(false);
    } catch {
      getError("Error al finalizar el producto");
    } finally {
      setGuardandoCambios(false);
    }
  };



  return {
    productosFiltrados,
    cargando,
    dialogoAbierto,
    setDialogoAbierto,
    productoAEliminar,
    handleDeleteProduct,
    confirmDeleteProduct,
    handleViewProduct,
    handleProductSheetClose,
    setFichaAbierta,
    fichaAbierta,
    productoSeleccionado,
    actualizarServicio,
    guardarCambios,
    guardandoCambios,
    proveedoresLista,
    agregarServicio,
    onAddPassenger,
    eliminarPasajero,
    handleAddPagoPasajero,
    handleUpdatePagoEstudiante,
    handleEliminarPagoEstudiante,
    handleToggleRevisadoEstudiante,
    onUpdatePassenger,
    nuevoPasajero,
    setNuevoPasajero,
    nuevoPago,
    setNuevoPago,
    senaAbierta,
    setSenaAbierta,
    handleOpenSena,
    productoEnSena,
    handleUpdateMontoSena,
    nuevaObservacion, 
    setNuevaObservacion,
    actualizarProductoSeleccionado,
    setProductoSeleccionado,
    setProductoEnSena,
    handleFinalizarProducto,
    cargarProductos,
  };
};
// Hook finalizado y limpio de errores.

